import { FeatureCollection } from "geojson";
import { OgcParams } from "../models/geo/ogcParams";

import { Discipline } from "../models/discipline";

import { BaseService } from "./base.service";
import { retryWithAuth } from "./utils/retryWithAuth";
import { AssetsWebService } from "../webservices/assets.web.service";
import { PagedResult } from "../models/pagedResult";
import { LayerAsset } from "../models/maps/layerAsset";
import { ListParams } from "../models/listParams";


export class AssetsService extends BaseService {
  
  // Private static instance of the class (singleton)
  private static instance: AssetsService;
  public disciplines: Discipline[] | null = null;
  public organisaties: any[] | null = null;
  public planStatusses: any[] | null = null;
  public planTypes: any[] | null = null;
  public beoordelingStatusses: any[] | null = null;

  // Private constructor to prevent direct instantiation
  private constructor() {
    super();
  }

  // Method to return the singleton instance
  public static getInstance(): AssetsService {
    if (!AssetsService.instance) {
      AssetsService.instance = new AssetsService();
    }
    return AssetsService.instance;
  }

  public async getAssetsLayers(params: ListParams): Promise<PagedResult<LayerAsset>> {
    const assetsWebService = new AssetsWebService();
    
    if (!params) {
      params = {
        _offset: 0,
        _limit: 25,
      };
    }

    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth<PagedResult<LayerAsset>>(
        () => assetsWebService.getAssetsLayers(organisatie.id, params),
        2,
        { redirectOnForbidden: false }
      )
    ).catch((error: any) => {
      if (this.isForbiddenError(error)) {
        console.warn('[AssetsService] 403 ontvangen voor de huidige rol bij GET /api/v1/asset-layers; fallback naar lege lijst.');
        return {
          count: 0,
          offset: params?._offset ?? 0,
          limit: params?._limit ?? 0,
          items: [],
        };
      }

      throw error;
    });
  }

  // Assets OGC features

  public async getAssetLayerOgcFeaturesSchema(assetsLayerId: string, ogcParams: OgcParams) {
    const assetsWebService = new AssetsWebService();

    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => assetsWebService.getAssetsOgcFeaturesCollectionsSchema(assetsLayerId, organisatie.id), 2)
    );
  }

  public async getAssetLayerOgcFeatures(assetsLayerId: string, ogcParams: OgcParams): Promise<FeatureCollection> {
    const assetsWebService = new AssetsWebService();
    const organisatie = await this.getOrganisatieOrReject();
    // console.log("Fetching OGC features with params:", ogcParams);

    const buildPageParams = (offset: number): OgcParams => {
      const p = new OgcParams();
      p.bbox = ogcParams.bbox;
      p.limit = ogcParams.limit;
      p.offset = offset;
      // console.log("Built OGC params for page fetch:", p);
      return p;
    };

    const fetchPage = (params: OgcParams) =>
      retryWithAuth(
        () => assetsWebService.getAssetsOgcFeaturesCollectionsItems(assetsLayerId, organisatie.id, params),
        2,
        { redirectOnNotFound: false }
      );

    const firstPage = await fetchPage(buildPageParams(ogcParams.offset));
    const allFeatures = [...(firstPage.features ?? [])];

    const numberMatched = (firstPage as any).numberMatched as number | undefined;
    if (numberMatched && numberMatched > allFeatures.length) {
      let offset = allFeatures.length;
      while (offset < numberMatched) {
        const page = await fetchPage(buildPageParams(offset));
        const pageFeatures = page.features ?? [];
        if (pageFeatures.length === 0) break;
        allFeatures.push(...pageFeatures);
        offset += pageFeatures.length;
      }
    }

    return { ...firstPage, features: allFeatures };
  }


};

// THIS IS THE KEY PART: 
// Export an instance of the PlanService class
export const assetsService = AssetsService.getInstance();